import { alpha, type SxProps, type Theme, type TypographyProps } from '@mui/material';
import { SkinStylesMap } from '../../hooks/useSkinStyles';
import { SKIN } from '../../types/enums';

type OutcomeTableCellBaseStyles = {
  outcomeCell: SxProps<Theme>;
  oddsStack: SxProps<Theme>;
  lockIcon: SxProps<Theme>;
  mixedMarkets: SxProps<Theme>;
};

const HIDE_TYPOGRAPHY_OVERFLOW: SxProps<Theme> = {
  '& .MuiTypography-root': {
    overflow: 'hidden',
  },
};

const NEOBET_OUTCOME_TRANSITION = 'transform 200ms ease-out';

const getNeobetOutcomeBoxShadow =
  (selected: boolean, pressed = false) =>
  (theme: Theme) => {
    const lowerShadowSize = pressed ? 1 : 2;
    const upperShadowSize = pressed && !selected ? 0 : 1;

    return selected
      ? `0 -${lowerShadowSize}px 0 0 ${theme.palette.primary[700]} inset, 0 1px 0 0 ${theme.palette.primary[400]} inset`
      : `0 -${lowerShadowSize}px 0 0 ${theme.palette.neutral[800]} inset, 0 ${upperShadowSize}px 0 0 ${alpha(
          theme.palette.neutral[500],
          0.5
        )} inset`;
  };

const NEOBET_SELECTED_ODDS_TEXT_STYLES = {
  paintOrder: 'stroke fill',
  WebkitTextStroke: (theme: Theme) => `1.5px ${theme.palette.primary[700]}`,
  textShadow: (theme: Theme) => `0 1.5px 0 ${theme.palette.primary[700]}`,
  px: 0.125,
  pb: 0.125,
} satisfies SxProps<Theme>;

const getNeobetOutcomeSurfaceStyles = (selected: boolean) => ({
  boxShadow: getNeobetOutcomeBoxShadow(selected),
  transform: 'translateY(0)',
  transition: NEOBET_OUTCOME_TRANSITION,
  '&:active': {
    transform: 'translateY(1px)',
    boxShadow: getNeobetOutcomeBoxShadow(selected, true),
    transition: 'transform 200ms ease-in',
  },
});

export type OutcomeTableCellSkinStyles = OutcomeTableCellBaseStyles & {
  oddsColor: TypographyProps['color'];
  labelColor: TypographyProps['color'];
  specialValueColor: TypographyProps['color'];
  allowTextWrap: boolean;
};

const BET365_STYLES: OutcomeTableCellSkinStyles = {
  outcomeCell: {
    flex: 1,
    color: 'primary.600',
    backgroundColor: 'transparent',
    opacity: 1,
    cursor: 'pointer',
    WebkitTapHighlightColor: 'transparent',
    py: {
      xs: 1.5,
      md: 2,
    },
    px: {
      xs: 1.5,
      md: 2,
    },
    '&:hover': {
      backgroundColor: 'neutral.600',
    },
    '& span': {
      typography: {
        xs: 'body2',
        md: 'body2',
      },
      letterSpacing: {
        xs: 0,
      },
    },
    display: 'flex',
    alignItems: 'center',
    position: 'relative',
    minWidth: 0,
    height: '100%',
  },
  oddsStack: {
    alignItems: 'center',
    rowGap: 0.25,
    columnGap: 0.5,
    width: '100%',
  },
  lockIcon: {
    color: 'neutral.400',
    fontSize: {
      xs: '0.75rem',
      md: '1.25rem',
    },
  },
  mixedMarkets: {
    justifyContent: 'center',
    flexDirection: {
      xs: 'column',
      md: 'row',
    },
    width: '100%',
    p: {
      xs: 0.5,
      md: 0.5,
    },
    '& span': {
      typography: {
        xs: 'body4',
        md: 'body2',
      },
    },
  },
  oddsColor: 'primary.600',
  labelColor: 'primary.600',
  specialValueColor: 'primary.600',
  allowTextWrap: false,
};

const makeOutcomeTableCellStyles = (selected: boolean, disabled?: boolean): OutcomeTableCellSkinStyles => {
  const defaultColor = selected ? 'primary.600' : 'secondary.main';
  const defaultLabelColor = selected ? 'neutral.600' : 'neutral.25';

  return {
    ...BET365_STYLES,
    outcomeCell: {
      ...BET365_STYLES.outcomeCell,
      color: defaultColor,
      backgroundColor: selected ? 'neutral.100' : 'transparent',
      opacity: disabled ? 0.3 : 1,
      cursor: disabled ? 'not-allowed' : 'pointer',
      '&:hover': {
        backgroundColor: disabled ? 'neutral.800' : selected ? 'neutral.100' : 'neutral.600',
      },
    },
    oddsColor: defaultColor,
    labelColor: defaultLabelColor,
    specialValueColor: defaultLabelColor,
  };
};

const makeGolaOutcomeTableCellStyles = (
  selected: boolean,
  disabled?: boolean,
  mixedMarket?: boolean
): OutcomeTableCellSkinStyles => {
  const defaultStyles = makeOutcomeTableCellStyles(selected, disabled);
  return {
    ...defaultStyles,
    outcomeCell: {
      ...defaultStyles.outcomeCell,
      justifyContent: 'center',
      ...(!mixedMarket && {
        borderRight: '1px solid',
        borderColor: (theme) => alpha(theme.palette.neutral[400], 0.25),
        '&:last-child': {
          borderRight: 'none',
        },
      }),
    },
    oddsStack: {
      ...defaultStyles.oddsStack,
      justifyContent: 'center',
    },
  };
};

const makeGolaSearchTableCellStyles = (selected: boolean, disabled?: boolean): OutcomeTableCellSkinStyles => {
  const defaultStyles = makeOutcomeTableCellStyles(selected, disabled);
  return {
    ...defaultStyles,
    outcomeCell: {
      ...defaultStyles.outcomeCell,
      color: 'info.main',
      py: {
        xs: 0.5,
        md: 0.5,
      },
      px: {
        xs: 1.5,
        md: 2,
      },
      fontSize: '13px',
      '& #outcome-name': {
        fontSize: '11px',
        opacity: 0.5,
      },
      '& span': {
        fontSize: '13px',
      },
    },
    oddsStack: {
      ...defaultStyles.oddsStack,
      justifyContent: 'center',
    },
    oddsColor: 'info.main',
  };
};

const makeNeobetOutcomeTableCellStyles = (
  selected: boolean,
  disabled?: boolean,
  mixedMarket?: boolean,
  featuredCardVariant?: boolean
): OutcomeTableCellSkinStyles => {
  const defaultStyles = makeOutcomeTableCellStyles(selected, disabled);

  const featuredCardHoverColor = ((): string => {
    if (selected && disabled) return 'primary.600';
    if (selected) return 'primary.500';
    if (disabled) return 'neutral.800';
    return 'neutral.700';
  })();

  const defaultHoverColor = ((): string => {
    if (selected && disabled) return 'primary.600';
    if (selected) return 'primary.500';
    if (disabled) return 'neutral.700';
    return 'neutral.500';
  })();

  if (featuredCardVariant) {
    return {
      ...defaultStyles,
      outcomeCell: {
        ...defaultStyles.outcomeCell,
        justifyContent: 'center',
        color: selected ? 'neutral.900' : 'neutral.25',
        backgroundColor: selected ? 'primary.600' : 'neutral.800',
        borderRadius: '10px',
        ...getNeobetOutcomeSurfaceStyles(selected),
        py: {
          xs: 0.75,
          md: 0.875,
        },
        minHeight: { xs: 38, md: 40 },
        '&:hover': {
          backgroundColor: selected ? 'primary.600' : 'neutral.800',
        },
        '@media (hover: hover) and (pointer: fine)': {
          '&:hover': {
            backgroundColor: featuredCardHoverColor,
          },
        },
        '& .MuiStack-root': {
          width: '100%',
          minWidth: 0,
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          rowGap: 0,
          columnGap: 0.5,
        },
        '& #outcome-name': {
          typography: 'body3',
          fontWeight: 700,
          color: selected ? 'neutral.900' : 'neutral.25',
          minWidth: 0,
          flex: '1 1 auto',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          textAlign: 'left',
        },
        '& .MuiStack-root > span:last-of-type': {
          typography: 'body3',
          fontWeight: 800,
          color: selected ? 'neutral.25' : 'primary.main',
          flexShrink: 0,
          whiteSpace: 'nowrap',
          textAlign: 'right',
          ...(selected && NEOBET_SELECTED_ODDS_TEXT_STYLES),
        },
        ...HIDE_TYPOGRAPHY_OVERFLOW,
      },
      oddsStack: {
        ...defaultStyles.oddsStack,
        justifyContent: 'space-between',
        alignItems: 'center',
        minWidth: 0,
        rowGap: 0,
        columnGap: 0.5,
      },
      oddsColor: selected ? 'neutral.25' : 'primary.main',
      labelColor: selected ? 'neutral.900' : 'text.secondary',
      specialValueColor: selected ? 'neutral.900' : 'text.secondary',
      allowTextWrap: true,
    };
  }

  return {
    ...defaultStyles,
    outcomeCell: {
      ...defaultStyles.outcomeCell,
      justifyContent: 'center',
      color: selected ? 'neutral.900' : 'neutral.25',
      backgroundColor: selected ? 'primary.600' : 'neutral.700',
      borderRadius: '10px',
      ...getNeobetOutcomeSurfaceStyles(selected),
      py: {
        xs: 1,
        md: 1.125,
      },
      '&:hover': {
        backgroundColor: selected ? 'primary.600' : 'neutral.700',
      },
      '@media (hover: hover) and (pointer: fine)': {
        '&:hover': {
          backgroundColor: defaultHoverColor,
        },
      },
      '& span': {
        typography: {
          xs: 'body2Compact',
          md: 'body2',
        },
        fontWeight: { xs: 600, md: 600 },
        letterSpacing: { xs: 0, md: 0 },
      },
      '& #outcome-name': {
        fontWeight: 600,
        minWidth: 0,
        flex: '0 1 auto',
        maxWidth: '100%',
        whiteSpace: 'normal',
        wordBreak: 'normal',
        overflowWrap: 'normal',
      },
      '& .MuiStack-root > span:last-child': {
        fontWeight: 800,
        flexShrink: 0,
        whiteSpace: 'nowrap',
        ...(selected && NEOBET_SELECTED_ODDS_TEXT_STYLES),
      },
      ...HIDE_TYPOGRAPHY_OVERFLOW,
      ...(!mixedMarket && {
        minHeight: { xs: 44, md: 48 },
      }),
      '& .MuiStack-root': {
        alignItems: 'center',
        columnGap: 0.75,
      },
      '& .MuiStack-root:has(#outcome-name)': {
        justifyContent: 'space-between',
      },
    },
    oddsStack: {
      ...defaultStyles.oddsStack,
      justifyContent: 'center',
      minWidth: 0,
      rowGap: 0.25,
    },
    mixedMarkets: {
      ...defaultStyles.mixedMarkets,
      borderRadius: '10px',
      minHeight: '100%',
      px: {
        xs: 0.25,
        md: 0.5,
      },
      '& span': {
        typography: {
          xs: 'body4',
          md: 'body3',
        },
        fontWeight: { xs: 600, md: 600 },
      },
      '& #outcome-name': {
        display: '-webkit-box',
        WebkitBoxOrient: 'vertical',
        WebkitLineClamp: 2,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      },
      '& .MuiStack-root': {
        flexDirection: {
          xs: 'column',
          md: 'row',
        },
        justifyContent: 'center',
        columnGap: 0.5,
      },
      '& .MuiStack-root:has(#outcome-name)': {
        justifyContent: 'center',
      },
    },
    lockIcon: {
      color: 'neutral.400',
      fontSize: {
        xs: '0.75rem',
        md: '1rem',
      },
    },
    oddsColor: selected ? 'neutral.25' : 'primary.main',
    labelColor: selected ? 'neutral.900' : 'neutral.25',
    specialValueColor: selected ? 'neutral.900' : 'neutral.25',
    allowTextWrap: true,
  };
};

export const getOutcomeTableCellSkinStyles = (
  selected: boolean,
  disabled?: boolean,
  searchVariant?: boolean,
  mixedMarket?: boolean,
  featuredCardVariant?: boolean
): SkinStylesMap<OutcomeTableCellSkinStyles> => ({
  [SKIN.BET365]: () => makeOutcomeTableCellStyles(selected, disabled),
  [SKIN.GOLA]: () =>
    searchVariant
      ? makeGolaSearchTableCellStyles(selected, disabled)
      : makeGolaOutcomeTableCellStyles(selected, disabled, mixedMarket),
  [SKIN.NEOBET]: () => makeNeobetOutcomeTableCellStyles(selected, disabled, mixedMarket, featuredCardVariant),
});
